import { FC } from 'react';

import { Icon } from '@purpur/library/icon';
import { focus } from '@purpur/library/icon/assets/focus';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { Suggestion } from '@telialt/common-input-with-suggestions/src';
import { SuggestionsProps } from '@telialt/common-input-with-suggestions/src/components/Suggestions/Suggestions';

import { City, Salon } from '../../model';
import { RetailsTestID } from '../../test-ids';
import { findNearestSalon } from '../../utils';
import {
    PositionSuggestionContainer,
    PositionSuggestionLabel,
    QuickLinksLabelsContainer,
    SuggestionsList,
    SuggestionsWrapper,
} from './CitySearch.styles';
import { SuggestionItem } from './SuggestionItem';

type Props = {
    suggestions: Suggestion[];
    allCities: City[];
    onSelect: (suggestion: Suggestion) => void;
    onSalonSelect: (salon: Salon) => void;
} & SuggestionsProps;

export const QuickLinks: FC<Props> = ({ suggestions, allCities, onSelect, onSalonSelect, id }) => {
    const { t } = useTranslations();

    const handlePositionSelect = async () => {
        const nearestSalon = await findNearestSalon(allCities);
        onSelect({ value: nearestSalon.cityId, label: nearestSalon.city });
        onSalonSelect(nearestSalon);
    };

    return (
        <SuggestionsWrapper data-test={RetailsTestID.CITY_SEARCH_QUICK_LINKS}>
            <PositionSuggestionContainer>
                <SuggestionItem
                    label={
                        <PositionSuggestionLabel>
                            <Icon svg={focus} size="md" /> {t['retails_map.search.closest']}
                        </PositionSuggestionLabel>
                    }
                    onClick={handlePositionSelect}
                />
            </PositionSuggestionContainer>
            <QuickLinksLabelsContainer>
                <div>{t['retails_map.search.most_popular']}</div>
                <div>{t['retails_map.search.count']}</div>
            </QuickLinksLabelsContainer>
            <SuggestionsList id={id} role="listbox" aria-label={t['retails_map.search.results_label']}>
                {suggestions.map((suggestion, index) => (
                    <SuggestionItem
                        key={`${suggestion.value}-${index}`}
                        label={suggestion.label}
                        count={allCities.find((city) => city.id === suggestion.value)?.count}
                        onClick={() => onSelect(suggestion)}
                    />
                ))}
            </SuggestionsList>
        </SuggestionsWrapper>
    );
};
